import React, {useEffect, useRef, useState} from "react";
import {useCopyToClipboard} from "react-use";
import {BlankButton, Icon, styled} from "@reside/ui";

import {Tooltip} from "../tooltip";

const COPIED_FEEDBACK_MS = 1000;

type Props = Readonly<{
  value: string;
  testId?: string;
}>;

export const CopyToClipboardIconButton = ({value, testId}: Props) => {
  const [clipboard, copyToClipboard] = useCopyToClipboard();
  const [showCopied, setShowCopied] = useState(false);
  const [showError, setShowError] = useState(false);
  const copiedTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!clipboard.error) {
      return undefined;
    }

    setShowError(true);
    setShowCopied(false);
    const timeoutId = window.setTimeout(
      () => setShowError(false),
      COPIED_FEEDBACK_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [clipboard.error]);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current != null) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    },
    [],
  );

  const handleCopy = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowError(false);
    copyToClipboard(value);
    setShowCopied(true);

    if (copiedTimeoutRef.current != null) {
      window.clearTimeout(copiedTimeoutRef.current);
    }

    copiedTimeoutRef.current = window.setTimeout(() => {
      setShowCopied(false);
      copiedTimeoutRef.current = null;
    }, COPIED_FEEDBACK_MS);
  };

  const tooltip = showError
    ? "Unable to copy"
    : showCopied
    ? "Copied!"
    : "Copy ID";
  const showFeedback = showCopied || showError;

  return (
    <CopyButtonWrap>
      <Tooltip
        content={tooltip}
        {...(showFeedback ? {isOpen: true} : {})}
      >
        <CopyButton
          type="button"
          data-dashboard-card-stop=""
          data-test-id={testId}
          onClick={handleCopy}
        >
          <CopyIcon name="duplicate" size={13} color="gray100" />
        </CopyButton>
      </Tooltip>
    </CopyButtonWrap>
  );
};

const CopyButtonWrap = styled.span`
  transition: opacity 0.12s ease, visibility 0.12s ease;

  [data-dashboard-cell] & {
    opacity: 0;
    visibility: hidden;
  }

  [data-dashboard-cell]:hover &,
  [data-dashboard-cell]:focus-within &,
  &:focus-within {
    opacity: 1;
    visibility: visible;
  }

  @media (hover: none) {
    [data-dashboard-cell] & {
      opacity: 1;
      visibility: visible;
    }
  }
`;

const CopyIcon = styled(Icon)`
  transform: scale(1.08);
  transform-origin: center center;
`;

const CopyButton = styled(BlankButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  margin-left: 8px;
  border-radius: 4px;
  line-height: 0;
  vertical-align: middle;

  &:hover {
    background-color: ${({theme}) => theme.color.borderLightGray};
  }
`;
